import { createAsyncThunk } from '@reduxjs/toolkit';

import { setLoading } from 'src/components/common/loading/loadingSlice';
import {
	IStudyDetails,
	IGetStudyDetailsThunkArgs,
	ICreateStudyThunkArgs,
	IGetStudiesThunkArgs,
	IStudyBase,
	IUpdateStudyThunkArgs,
	IStudyWithImages,
	IGetStudyWithImagesThunkArgs,
	IInferenceResult,
	IRunInferenceThunkArgs,
	IDeleteStudyThunkArgs,
} from 'src/features/study/domain/types';
import { studyApi } from 'src/features/study/infrastructure/api';
import { IStudyWithAssignment } from 'src/features/study_assignment/domain/types';
import { handleThunkError } from 'src/utils/handleThunkError';

export const STUDY_LOADING_KEY = 'study';
export const AI_INFERENCE_LOADING_KEY = 'ai_inference';

export const getStudyDetails = createAsyncThunk<
	IStudyDetails,
	IGetStudyDetailsThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'study/getStudyDetails',
	async (
		{ studyId, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(setLoading({ key: STUDY_LOADING_KEY, loading: true }));
			const response = await studyApi.getStudyDetails(studyId, siteSlug);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(
				error,
				'Failed to fetch study details',
			);
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(setLoading({ key: STUDY_LOADING_KEY, loading: false }));
		}
	},
);

export const createStudy = createAsyncThunk<
	IStudyDetails,
	ICreateStudyThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'study/createStudy',
	async (
		{ request, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(setLoading({ key: STUDY_LOADING_KEY, loading: true }));
			const response = await studyApi.createStudy(request, siteSlug);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(error, 'Failed to create study');
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(setLoading({ key: STUDY_LOADING_KEY, loading: false }));
		}
	},
);

export const getStudies = createAsyncThunk<
	IPaginatedResponse<IStudyWithAssignment>,
	IGetStudiesThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'study/getStudies',
	async (
		{ request, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({
					key: STUDY_LOADING_KEY,
					loading: true,
				}),
			);
			const response = await studyApi.getStudies(request, siteSlug);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(error, 'Failed to fetch studies');
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({
					key: STUDY_LOADING_KEY,
					loading: false,
				}),
			);
		}
	},
);

export const updateStudy = createAsyncThunk<
	IStudyBase,
	IUpdateStudyThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'study/updateStudy',
	async (
		{ studyId, request, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(setLoading({ key: STUDY_LOADING_KEY, loading: true }));
			const response = await studyApi.updateStudy(
				studyId,
				request,
				siteSlug,
			);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(error, 'Failed to update study');
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(setLoading({ key: STUDY_LOADING_KEY, loading: false }));
		}
	},
);

export const getStudyWithImages = createAsyncThunk<
	IStudyWithImages,
	IGetStudyWithImagesThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'study/getStudyWithImages',
	async (
		{ studyId, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(setLoading({ key: STUDY_LOADING_KEY, loading: true }));
			const response = await studyApi.getStudyWithImages(
				studyId,
				siteSlug,
			);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(
				error,
				'Failed to get study with images',
			);
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(setLoading({ key: STUDY_LOADING_KEY, loading: false }));
		}
	},
);

export const runInference = createAsyncThunk<
	IInferenceResult[],
	IRunInferenceThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'study/runInference',
	async (
		{ studyId, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({ key: AI_INFERENCE_LOADING_KEY, loading: true }),
			);
			const response = await studyApi.runInference(studyId, siteSlug);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(error, 'Failed to run AI inference');
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({ key: AI_INFERENCE_LOADING_KEY, loading: false }),
			);
		}
	},
);

export const deleteStudy = createAsyncThunk<
	void,
	IDeleteStudyThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'study/deleteStudy',
	async (
		{ studyId, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(setLoading({ key: STUDY_LOADING_KEY, loading: true }));
			await studyApi.deleteStudy(studyId, siteSlug);

			onSuccess?.();
			return;
		} catch (error: unknown) {
			const err = handleThunkError(error, 'Failed to delete study');
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(setLoading({ key: STUDY_LOADING_KEY, loading: false }));
		}
	},
);
